import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import { shared, ODO_LOGO_URL } from '@odo/shared-ui/clinical-pdf/styles';
import { dx, ACCENT } from './styles';
import { renderTiptap } from '@odo/shared-ui/rich-editor/tiptap-pdf';
import {
    STATIC_TEMPLATE_OPTIONS_TIPTAP,
    STATIC_NEXT_STEPS_TIPTAP,
    buildOrthopulseInfoTiptap,
    ORTHOPULSE_LIST_PRICE_FALLBACK
} from './templates';
import { ORTHOPULSE_PRODUCT_NAME } from './editor/editorState';
import type { DxReportFeeLine, DxReportFees, DxReportPayload } from './types';

interface DxReportDocumentProps {
    payload: DxReportPayload;
}

const SECTION_DEFAULT_TITLES: Record<string, string> = {
    'treatment-goals': 'Treatment goals',
    findings: 'Clinical findings',
    comments: 'Comments',
    risks: 'Risks & concerns',
    'special-consent': 'Special consent',
    'orthopulse-recommendation': 'Orthopulse recommendation',
    'estimated-treatment-time': 'Estimated treatment time'
};

const ORTHOPULSE_IMAGE_PATHS: readonly string[] = [
    '/assets/dx-report/orthopulse-device.jpg',
    '/assets/dx-report/orthopulse-in-use.jpg'
];

function formatDateLong(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateShort(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

function formatCurrency(cents: number, currency: 'USD'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
    }).format(cents / 100);
}

// Frozen list price + summed discount (rule names stay internal, not
// printed). Skipped once overridden - the frozen snapshot no longer matches.
function FeeDiscountRows({ line, currency }: { line: DxReportFeeLine; currency: 'USD' }) {
    if (line.isOverridden) return null;
    if (!line.discounts || line.discounts.length === 0 || line.listPriceCents == null) return null;
    const discountTotalCents = line.discounts.reduce((sum, d) => sum + d.deltaCents, 0);
    return (
        <View style={dx.feeDiscountBlock}>
            <View style={dx.feeDiscountLineRow}>
                <Text style={dx.feeDiscountRowLabel}>Product price</Text>
                <Text style={dx.feeListPrice}>{formatCurrency(line.listPriceCents, currency)}</Text>
            </View>
            <View style={dx.feeDiscountLineRow}>
                <Text style={dx.feeDiscountLabel}>Practice discount</Text>
                <Text style={dx.feeDiscountAmount}>
                    -{formatCurrency(discountTotalCents, currency)}
                </Text>
            </View>
        </View>
    );
}

function FeesBlock({ fees }: { fees: DxReportFees }) {
    const baseCents = fees.caseProduct?.amountCents ?? 0;
    const addonsTotal = fees.addons.reduce((sum, a) => sum + a.amountCents, 0);
    const odoTotalCents = baseCents + addonsTotal;

    return (
        <View>
            <View style={dx.feeTable}>
                {fees.caseProduct ? (
                    <View style={dx.feeRow} wrap={false}>
                        <View style={dx.feeLabelCol}>
                            <Text style={dx.feeLabel}>ODO Treatment Fee</Text>
                            <Text style={dx.feeSubLabel}>{fees.caseProduct.productName}</Text>
                            <FeeDiscountRows line={fees.caseProduct} currency={fees.currency} />
                        </View>
                        <Text style={dx.feeAmount}>{formatCurrency(baseCents, fees.currency)}</Text>
                    </View>
                ) : (
                    <View style={dx.feeRow} wrap={false}>
                        <View style={dx.feeLabelCol}>
                            <Text style={dx.feeLabel}>ODO Treatment Fee</Text>
                            <Text style={dx.feeSubLabel}>No case product selected</Text>
                        </View>
                        <Text style={dx.feeAmount}>{formatCurrency(0, fees.currency)}</Text>
                    </View>
                )}
                {fees.addons.map((addon, i) => (
                    <View key={`${addon.productId}-${i}`} style={dx.feeRow} wrap={false}>
                        <View style={dx.feeLabelCol}>
                            <Text style={dx.feeLabel}>{addon.productName}</Text>
                            <Text style={dx.feeSubLabel}>Optional add-on</Text>
                            <FeeDiscountRows line={addon} currency={fees.currency} />
                        </View>
                        <Text style={dx.feeAmount}>
                            {formatCurrency(addon.amountCents, fees.currency)}
                        </Text>
                    </View>
                ))}

                <View style={dx.feeSubtotalRow} wrap={false} minPresenceAhead={120}>
                    <View style={dx.feeLabelCol}>
                        <Text style={dx.feeSubtotalLabel}>ODO Total</Text>
                        <Text style={dx.feeSubLabel}>Base fee + selected add-ons</Text>
                    </View>
                    <Text style={dx.feeSubtotalAmount}>
                        {formatCurrency(odoTotalCents, fees.currency)}
                    </Text>
                </View>

                <View style={dx.feeTotalRow} wrap={false} minPresenceAhead={80}>
                    <View style={dx.feeLabelCol}>
                        <Text style={dx.feeTotalLabel}>Recommended Patient Investment</Text>
                    </View>
                    <Text style={dx.feeTotalAmount}>
                        {formatCurrency(fees.recommendedPatientInvestmentCents, fees.currency)}+
                    </Text>
                </View>
            </View>
            {fees.notes && <Text style={dx.feeNotes}>{fees.notes}</Text>}
        </View>
    );
}

function SectionBlock({
    title,
    children,
    boxed = false
}: {
    title: string;
    children: React.ReactNode;
    boxed?: boolean;
}) {
    return (
        <View style={{ marginTop: 18 }} minPresenceAhead={20}>
            <Text style={shared.sectionTitle}>{title}</Text>
            {boxed ? (
                <>
                    <View
                        style={{
                            width: 40,
                            height: 2,
                            backgroundColor: ACCENT.amber,
                            marginBottom: 6
                        }}
                    />
                    <View style={[shared.notesBox, { minHeight: 0 }]}>{children}</View>
                </>
            ) : (
                <>
                    <View
                        style={{
                            width: 40,
                            height: 2,
                            backgroundColor: ACCENT.amber,
                            marginBottom: 6
                        }}
                    />
                    {children}
                </>
            )}
        </View>
    );
}

function DxReportDocument({ payload }: DxReportDocumentProps) {
    const { patientInfo, sections, fees } = payload;

    // Quote the same billed figure the fee table below prints, so the prose
    // and the table never contradict. Falls back to list prices when absent.
    const orthopulseFee = fees.addons.find((a) => a.productName === ORTHOPULSE_PRODUCT_NAME);
    const orthopulseCostLabel = orthopulseFee
        ? formatCurrency(orthopulseFee.amountCents, fees.currency)
        : ORTHOPULSE_LIST_PRICE_FALLBACK;

    // Only skip the static "About Orthopulse" page on an explicit "not needed"
    // choice; legacy drafts without the field are treated as recommended.
    const includeOrthopulseInfo = payload.orthopulseStatus !== 'not_needed';

    return (
        <Document>
            <Page size="LETTER" style={shared.page}>
                <View style={shared.pageContent}>
                    {/* Header — title left, logo right */}
                    <View style={shared.header}>
                        <Text style={shared.title}>Diagnostic Report</Text>
                        <Image src={ODO_LOGO_URL} style={shared.logo} />
                    </View>

                    {/* Identity block — single row: Date / Patient / DOB */}
                    <View style={[shared.infoRow, { gap: 12 }]}>
                        <View style={[shared.infoCell, { flex: 1, minWidth: 0 }]}>
                            <Text style={[shared.infoLabel, { width: 44 }]}>Date</Text>
                            <View style={shared.infoValue}>
                                <Text style={shared.infoValueText}>
                                    {formatDateLong(patientInfo.reportDate) || 'N/A'}
                                </Text>
                            </View>
                        </View>
                        <View style={[shared.infoCell, { flex: 1, minWidth: 0 }]}>
                            <Text style={[shared.infoLabel, { width: 44 }]}>Patient</Text>
                            <View style={shared.infoValue}>
                                <Text style={shared.infoValueText}>
                                    {patientInfo.patientName || 'N/A'}
                                </Text>
                            </View>
                        </View>
                        <View style={[shared.infoCell, { flex: 1, minWidth: 0 }]}>
                            <Text style={[shared.infoLabel, { width: 44 }]}>DOB</Text>
                            <View style={shared.infoValue}>
                                <Text style={shared.infoValueText}>
                                    {patientInfo.dateOfBirth
                                        ? `${formatDateShort(patientInfo.dateOfBirth)}${patientInfo.age != null ? ` (${patientInfo.age})` : ''}`
                                        : 'N/A'}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Identity block — second row: Doctor / Practice */}
                    <View style={[shared.infoRow, { gap: 12, marginTop: 10 }]}>
                        <View style={[shared.infoCell, { flex: 1, minWidth: 0 }]}>
                            <Text style={[shared.infoLabel, { width: 44 }]}>Doctor</Text>
                            <View style={shared.infoValue}>
                                <Text style={shared.infoValueText}>
                                    {patientInfo.doctorName || 'N/A'}
                                </Text>
                            </View>
                        </View>
                        <View style={[shared.infoCell, { flex: 1, minWidth: 0 }]}>
                            <Text style={[shared.infoLabel, { width: 44 }]}>Practice</Text>
                            <View style={shared.infoValue}>
                                <Text style={shared.infoValueText}>
                                    {patientInfo.practice || 'N/A'}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View
                        style={[
                            shared.divider,
                            { borderBottomColor: ACCENT.amber, borderBottomWidth: 1 }
                        ]}
                    />

                    {/* Dynamic sections */}
                    {sections.map((section) => {
                        const fallback = SECTION_DEFAULT_TITLES[section.id] ?? section.id;
                        const userTitle = section.title?.trim() || fallback;
                        return (
                            <SectionBlock key={section.id} title={userTitle} boxed>
                                {renderTiptap(section.tiptapJson, section.id, {
                                    accent: ACCENT.amber
                                })}
                            </SectionBlock>
                        );
                    })}

                    {/* About Orthopulse — own page; omitted when the
                        recommendation is explicitly "not needed" */}
                    {includeOrthopulseInfo && (
                        <View>
                            <SectionBlock title="About Orthopulse">
                                {renderTiptap(
                                    buildOrthopulseInfoTiptap(orthopulseCostLabel),
                                    'static-orthopulse-info',
                                    {
                                        spacingScale: 0.9,
                                        accent: ACCENT.amber
                                    }
                                )}
                                {ORTHOPULSE_IMAGE_PATHS.length >= 2 && (
                                    <View style={dx.galleryRow} wrap={false}>
                                        {ORTHOPULSE_IMAGE_PATHS.slice(0, 2).map((src) => (
                                            <Image key={src} src={src} style={dx.galleryImage} />
                                        ))}
                                    </View>
                                )}
                            </SectionBlock>
                        </View>
                    )}

                    {/* Static sections — force onto their own page */}
                    <View break>
                        <SectionBlock title="Template options">
                            {renderTiptap(
                                STATIC_TEMPLATE_OPTIONS_TIPTAP,
                                'static-template-options',
                                { accent: ACCENT.amber }
                            )}
                        </SectionBlock>

                        <SectionBlock title="What's next?">
                            {renderTiptap(STATIC_NEXT_STEPS_TIPTAP, 'static-next-steps', {
                                accent: ACCENT.amber
                            })}
                        </SectionBlock>
                    </View>

                    {/* Fees — force new page */}
                    <View break>
                        <SectionBlock title="Fees">
                            <FeesBlock fees={fees} />
                        </SectionBlock>
                    </View>
                </View>

                {/* Footer with page numbers */}
                <View style={shared.footer} fixed>
                    <Text
                        style={shared.footerText}
                        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
                    />
                </View>
            </Page>
        </Document>
    );
}

export { DxReportDocument };
